<template src="./review.html"></template>

<script lang="ts">
import formatName from 'filters/format-name'
import { addHighlightClass, truncateText } from 'util/text-utils'
import { RestError, ReviewProfileAttributes } from 'services/api-service'
import getReviewService from 'services/api/get-review-translated-data'
import GridLayout from 'layouts/grid/GridLayout.vue'
import { localizedLanguage } from 'util/localization-utils'
import logger from 'util/logger'
import MediaThumb from 'objects/media/MediaThumb.vue'
import ProductAbout from 'components/product-about/ProductAbout.vue'
import ReviewByline from 'objects/byline/ReviewByline.vue'
import ReviewDimension from 'components/review/Dimension.vue'
import ReviewResponses from 'components/review/ReviewResponses.vue'
import RatingStars from 'components/rating/RatingStars.vue'
import ReviewToolbar from 'components/review/ReviewToolbar.vue'
import standalonePopoverLoader from 'widgets/standalone-popover/standalone-popover-loader'
import { turnToConfig } from 'init/config'
import { TYPE_PHOTO } from 'util/media-utils'


interface IProfileAttribute {
  label: string
  value: string
}

export default {
  data() {
    return {
      // TODO: not sure how this is determined yet. May not be a data value
      areProfileAttributesStacked: false,
      // Just a single index (0) for review.text for now.  If ans when review updates are added,
      // a review may have more.
      // The index will be in the this list if the user clicked on the "Read More" for the text.
      expandedTextIndexes: [],
      isTranslated: false,
      showDetailsClicked: false
    }
  },
  props: {
    allItemsInLayout: Array,
    currentLayout: Object,
    enableFrontEndReviewTranslation: Boolean,
    // this is the highest heading level this component can have
    // should be 1 less than the widget header
    headingLevel: { type: Number, default: 3 },
    isRelatedReview: { type: Boolean, default: false },
    mediaSize: { type: Number, default: 60 },
    review: Object,
    showPurchaserBadge: Boolean,
    slotNamesAfterShowDetails: Array,
    truncateAllowance: { type: Number, default: 99 },
    truncateLength: { type: Number, default: 500 }
  },
  emits: ['openMediaModal', 'translateReview'],
  components: {
    'tt-grid-layout': GridLayout,
    'tt-media-thumb': MediaThumb,
    'tt-product-about': ProductAbout,
    'tt-review-byline': ReviewByline,
    'tt-dimension': ReviewDimension,
    'tt-review-responses': ReviewResponses,
    'tt-rating-stars': RatingStars,
    'tt-review-toolbar': ReviewToolbar
  },
  computed: {
    displayShowDetails() {
      return !this.showDetailsClicked && ~this.allItemsInLayout.indexOf('showDetails') &&
        this.hiddenSlotNames.length > 0
    },
    heading() {
      return {
        text: addHighlightClass(this.review.title),
        level: this.headingLevel
      }
    },
    // in mobile layout, if 'showDetails' item is added, everything below it is initially hidden
    // from view.  When 'showDetails' is clicked, the items below it are shown
    hiddenSlotNames() {
      if (!this.showDetailsClicked) {
        return this.slotNamesAfterShowDetails.filter((slotName) => {
          // we only want to include items that have data to be presented.  For example,
          // customDim-5 should not be included if there are only 4 custom dims.
          if (slotName.indexOf('customDim') === 0) {
            const dimNum = parseInt(slotName.split('-')[1], 10)
            return this.review.dimensions.length >= dimNum
          } else if (Object.prototype.hasOwnProperty.call(this.slotHasData, slotName)) {
            return this.slotHasData[slotName]
          }
          // all other slots will always have data for display
          return true
        })
      }
      return []
    },
    profileAttributes() {
      const attributesForDisplay = [] as Array<IProfileAttribute>
      const attributes = this.review.profileAttributes as ReviewProfileAttributes
      if (!attributes) {
        return attributesForDisplay
      }
      if (attributes.ageRange) {
        const label = this.$msg('review.attribute.age')
        attributesForDisplay.push(
          { label, value: attributes.ageRange }
        )
      }
      if (attributes.location) {
        const label = this.$msg('review.attribute.location')
        attributesForDisplay.push(
          { label, value: attributes.location }
        )
      }
      if (attributes.custom) {
        attributes.custom.forEach((item) => {
          attributesForDisplay.push(
            { label: item.label, value: item.value }
          )
        })
      }
      return attributesForDisplay
    },
    reviewDate() {
      return this.review.dateCreatedFormatted
    },
    reviewDimensions() {
      return this.review.dimensions.filter((dim, i) => {
        const slotName = `customDim-${(i + 1)}`
        dim.slotName = slotName
        return !this.isSlotHidden(slotName)
      })
    },
    reviewTexts() {
      const reviewTexts = []
      if (this.review.text) {
        const truncatedText =
          truncateText(this.review.text, this.truncateLength, this.truncateAllowance)
        const isExpanded = this.expandedTextIndexes.indexOf(0) > -1
        const text =
          addHighlightClass(truncatedText && !isExpanded ? truncatedText : this.review.text)
        reviewTexts.push({
          text,
          hasTruncatedText: !!truncatedText,
          updateDateFormatted: this.review.dateCreatedFormatted,
          isExpanded
        })
      }
      return reviewTexts
    },
    slotHasData() {
      return {
        relatedProduct: this.isRelatedReview,
        reviewTitle: this.review.title,
        media: this.review.media && this.review.media.length > 0,
        reviewText: this.reviewTexts.length > 0,
        profileAttributes: this.profileAttributes.length > 0,
        reviewResponses: this.review.responsesBySource.length > 0,
        purchasedOn: this.review.purchaseDateFormatted,
        syndication: this.syndicationAttribution,
        incentivized: this.review.incentivized,
        translate: this.translatable
      }
    },
    syndicationAttribution() {
      const syndication = this.review.syndication
      if (syndication) {
        let source
        if (syndication.logoUrl) {
          source = `<img src="${syndication.logoUrl}" alt="${syndication.sourceName}" ` +
            'class="tt-c-review__syndication-logo">'
        } else {
          source = syndication.sourceName
        }
        if (syndication.siteUrl) {
          source = `<a href="${syndication.siteUrl}" class="tt-c-review__syndication-link" ` +
            `target="_blank" rel="nofollow">${source}</a>`
        }
        return this.$msg('review.syndicationAttribution', [source])
      }
      return ''
    },
    textUpdates() {
      if (this.hasUpdates) {
        return this.review.updates.filter(update => update.text !== '' && update.text !== undefined)
      }
      return []
    },
    titleId() {
      return `tt-review-title-${this.review.id}`
    },
    translatable() {
      return this.isTranslated || (this.enableFrontEndReviewTranslation && this.review.locale &&
        localizedLanguage(this.review.locale) !== localizedLanguage(turnToConfig.locale))
    },
    translateMsg() {
      return (this.isTranslated ? this.$msg('review.showOriginal') : this.$msg('review.translate'))
    }
  },
  methods: {
    formatUserName(user) {
      return formatName(user)
    },
    isSlotHidden(slotName) {
      return ~this.hiddenSlotNames.indexOf(slotName)
    },
    mediaLinkTitle(mediaItem) {
      const username = formatName(mediaItem.user)
      const type = mediaItem.type === TYPE_PHOTO ? 'photo' : 'video'
      return this.$msg(`review.opensMediaModal.${type}`, [username])
    },
    openMediaModal(mediaIndex) {
      this.review.media.forEach((mediaItem) => {
        // media do not have catItem populated.  Need to make sure they do before opening in modal
        if (!mediaItem.catItem) {
          mediaItem.catItem = this.review.catItem
        }
        // also need to populate basic review data for the modal
        if (!mediaItem.review) {
          mediaItem.review = {
            rating: this.review.rating,
            text: this.review.text,
            title: this.review.title
          }
        }
      })
      this.$emit('openMediaModal', this.review.media, mediaIndex)
    },
    onShowDetailsClick() {
      this.showDetailsClicked = true
    },
    toggleExpandText(index) {
      // if it is in the list of expanded indexes, remove it, otherwise, add it
      const expandedIndex = this.expandedTextIndexes.indexOf(index)
      if (expandedIndex > -1) {
        this.expandedTextIndexes.splice(expandedIndex, 1)
      } else {
        this.expandedTextIndexes.push(index)
      }
    },
    translateReview() {
      if (this.isTranslated) {
        this.$emit('translateReview', this.review, this.isTranslated)
        this.isTranslated = false
        return
      }
      getReviewService.getReviewTranslatedData({
        id: this.review.id,
        locale: turnToConfig.locale
      }).then((data) => {
        this.$emit('translateReview', data, this.isTranslated)
        this.isTranslated = true
      }).catch((e: RestError) => {
        let msgKey =  'unexpected.error'
        if (e.errorCode === 'turnto-offline') {
          msgKey = 'offline.error'
        } else {
          logger.warn('Error translating review.', e)
        }
        standalonePopoverLoader.load({ message: this.$msg(msgKey), type: 'error' })
      })
    }
  },
  watch: {
    currentLayout(newLayout, oldLayout) {
      // something is causing this to be called when the layout hasn't changed
      // so checking just in case
      if (newLayout !== oldLayout) {
        this.showDetails = false
        this.expandedTextIndexes = []
      }
    }
  }
}
</script>

<style lang="scss" src="./review.scss"></style>
